import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Separator } from '../components/ui/separator';
import { 
  User, 
  Globe,
  Loader2,
  Check,
  ImageIcon,
  Info
} from 'lucide-react';
import { getFullImageUrl } from '../lib/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SettingsPage = () => {
  const { user, language, setLanguage, updateProfile, isAdmin } = useAuth();
  const { t } = useTranslation(language);
  const nameParts = (user?.name || '').split(' ');
  const [firstName, setFirstName] = useState(nameParts[0] || '');
  const [lastName, setLastName] = useState(nameParts.slice(1).join(' ') || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);

  // Admin About Us state
  const [aboutData, setAboutData] = useState({
    name: '',
    title_en: '',
    title_fr: '',
    bio_en: '',
    bio_fr: '',
    image_url: '',
    cover_image_url: ''
  });
  const [savingAbout, setSavingAbout] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchAboutContent();
    }
  }, [isAdmin]);

  const fetchAboutContent = async () => {
    try {
      const res = await axios.get(`${API_URL}/content/profile`);
      if (res.data?.content) {
        setAboutData({
          name: res.data.content.name || '',
          title_en: res.data.content.title_en || '',
          title_fr: res.data.content.title_fr || '',
          bio_en: res.data.content.bio_en || '',
          bio_fr: res.data.content.bio_fr || '',
          image_url: res.data.content.image_url || '',
          cover_image_url: res.data.content.cover_image_url || ''
        });
      }
    } catch (error) {
      console.error('Error fetching about content:', error);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      await updateProfile({ name: fullName, email, phone });
      toast.success(t('profileUpdated'));
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    toast.success(language === 'fr' ? 'Langue mise a jour' : 'Language updated');
  };

  const handleSaveAbout = async (e) => {
    e.preventDefault();
    setSavingAbout(true);
    try {
      await axios.put(`${API_URL}/content/profile`, aboutData);
      toast.success(language === 'fr' ? 'Informations mises a jour' : 'About info updated');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSavingAbout(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error(language === 'fr' ? 'Format non supporté (PNG, JPG uniquement)' : 'Unsupported format (PNG, JPG only)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Fichier trop volumineux (max 5MB)' : 'File too large (max 5MB)');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAboutData(prev => ({ ...prev, image_url: res.data.url }));
      toast.success(language === 'fr' ? 'Photo uploadée' : 'Photo uploaded');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      toast.error(language === 'fr' ? 'Format non supporté (PNG, JPG uniquement)' : 'Unsupported format (PNG, JPG only)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'fr' ? 'Fichier trop volumineux (max 5MB)' : 'File too large (max 5MB)');
      return;
    }

    setUploadingCover(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAboutData(prev => ({ ...prev, cover_image_url: res.data.url }));
      toast.success(language === 'fr' ? 'Photo de couverture uploadée' : 'Cover photo uploaded');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setUploadingCover(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">{t('settings')}</h1>
        <p className="text-muted-foreground mt-1">
          {language === 'fr' ? 'Gerez vos preferences' : 'Manage your preferences'}
        </p>
      </div>

      {/* Profile Section */}
      <Card className="card-base" data-testid="profile-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('profile')}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Mettez a jour vos informations personnelles' : 'Update your personal information'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{language === 'fr' ? 'Prénom' : 'First Name'}</Label>
                <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="settings-firstname-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{language === 'fr' ? 'Nom' : 'Last Name'}</Label>
                <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="settings-lastname-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="settings-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{language === 'fr' ? 'Téléphone' : 'Phone'}</Label>
              <Input id="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 6 12 34 56 78" data-testid="settings-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Rôle' : 'Role'}</Label>
              <div className="flex items-center gap-2">
                <span className="badge-info capitalize">{user?.role === 'admin' ? t('admin_role') : t('owner')}</span>
              </div>
            </div>
            <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-profile-btn">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('loading')}</> : <><Check className="mr-2 h-4 w-4" />{t('save')}</>}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Separator />

      {/* Admin: About Us Section */}
      {isAdmin && (
        <>
          <Card className="card-base" data-testid="about-us-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Info className="h-5 w-5" />
                {language === 'fr' ? 'A Propos (Landing Page)' : 'About Us (Landing Page)'}
              </CardTitle>
              <CardDescription>
                {language === 'fr' ? 'Modifiez votre profil affiche sur la page d\'accueil' : 'Edit your profile displayed on the landing page'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveAbout} className="space-y-4">
                {/* Profile Picture */}
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Photo de profil' : 'Profile Picture'}</Label>
                  <div className="flex items-center gap-4">
                    {aboutData.image_url ? (
                      <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-primary/20">
                        <img 
                          src={getFullImageUrl(aboutData.image_url)} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.onerror = null; e.target.src = ''; e.target.parentElement.classList.add('bg-muted'); }}
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <label htmlFor="about-photo-upload" className="cursor-pointer">
                        <div className="inline-flex items-center gap-2 px-4 py-2 border border-input rounded-md text-sm font-medium hover:bg-accent transition-colors">
                          {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                          {language === 'fr' ? 'Changer la photo' : 'Change Photo'}
                        </div>
                      </label>
                      <input id="about-photo-upload" type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handlePhotoUpload} data-testid="about-photo-upload" />
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG (max 5MB)</p>
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="about-name">{language === 'fr' ? 'Nom affiche' : 'Display Name'}</Label>
                  <Input id="about-name" value={aboutData.name} onChange={(e) => setAboutData(prev => ({ ...prev, name: e.target.value }))} data-testid="about-name-input" />
                </div>

                {/* Cover Image */}
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Photo de couverture (droite)' : 'Cover Image (right side)'}</Label>
                  <div className="flex items-center gap-4">
                    {aboutData.cover_image_url ? (
                      <div className="w-32 h-24 rounded-lg overflow-hidden border-2 border-primary/20">
                        <img src={getFullImageUrl(aboutData.cover_image_url)} alt="Cover" className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      <div className="w-32 h-24 rounded-lg bg-muted flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <label htmlFor="cover-photo-upload" className="cursor-pointer">
                        <div className="inline-flex items-center gap-2 px-4 py-2 border border-input rounded-md text-sm font-medium hover:bg-accent transition-colors">
                          {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                          {language === 'fr' ? 'Changer la photo' : 'Change Photo'}
                        </div>
                      </label>
                      <input id="cover-photo-upload" type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleCoverUpload} data-testid="cover-photo-upload" />
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG (max 5MB)</p>
                    </div>
                  </div>
                </div>

                {/* Title EN */}
                <div className="space-y-2">
                  <Label htmlFor="about-title-en">{language === 'fr' ? 'Titre (Anglais)' : 'Title (English)'}</Label>
                  <Input id="about-title-en" value={aboutData.title_en} onChange={(e) => setAboutData(prev => ({ ...prev, title_en: e.target.value }))} data-testid="about-title-en-input" />
                </div>

                {/* Title FR */}
                <div className="space-y-2">
                  <Label htmlFor="about-title-fr">{language === 'fr' ? 'Titre (Francais)' : 'Title (French)'}</Label>
                  <Input id="about-title-fr" value={aboutData.title_fr} onChange={(e) => setAboutData(prev => ({ ...prev, title_fr: e.target.value }))} data-testid="about-title-fr-input" />
                </div>

                {/* Bio EN */}
                <div className="space-y-2">
                  <Label htmlFor="about-bio-en">{language === 'fr' ? 'Description (Anglais)' : 'Bio (English)'}</Label>
                  <Textarea id="about-bio-en" value={aboutData.bio_en} onChange={(e) => setAboutData(prev => ({ ...prev, bio_en: e.target.value }))} rows={3} data-testid="about-bio-en-input" />
                </div>

                {/* Bio FR */}
                <div className="space-y-2">
                  <Label htmlFor="about-bio-fr">{language === 'fr' ? 'Description (Francais)' : 'Bio (French)'}</Label>
                  <Textarea id="about-bio-fr" value={aboutData.bio_fr} onChange={(e) => setAboutData(prev => ({ ...prev, bio_fr: e.target.value }))} rows={3} data-testid="about-bio-fr-input" />
                </div>

                <Button type="submit" className="btn-primary" disabled={savingAbout} data-testid="save-about-btn">
                  {savingAbout ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('loading')}</> : <><Check className="mr-2 h-4 w-4" />{t('save')}</>}
                </Button>
              </form>
            </CardContent>
          </Card>
          <Separator />
        </>
      )}

      {/* Language Section */}
      <Card className="card-base" data-testid="language-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {t('language')}
          </CardTitle>
          <CardDescription>
            {language === 'fr' ? 'Choisissez votre langue preferee' : 'Choose your preferred language'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={language} onValueChange={handleLanguageChange} className="space-y-3">
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="en" id="en" data-testid="lang-en-radio" />
              <Label htmlFor="en" className="flex items-center gap-2 cursor-pointer">
                <span className="text-xl">GB</span>
                <span>{t('english')}</span>
              </Label>
            </div>
            <div className="flex items-center space-x-3">
              <RadioGroupItem value="fr" id="fr" data-testid="lang-fr-radio" />
              <Label htmlFor="fr" className="flex items-center gap-2 cursor-pointer">
                <span className="text-xl">FR</span>
                <span>{t('french')}</span>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
